import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { TechAuthProvider, useTechAuth } from '../../contexts/TechAuthContext';
import TechLoginForm from './components/TechLoginForm';
import TechRegisterForm from './components/TechRegisterForm';
import TechChatWindow from './components/TechChatWindow';
import { getConversationsForUser } from '../../services/chatService';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';

function TechPortalInner() {
  const { techUser, techProfile, loading, logout } = useTechAuth();
  const [authMode, setAuthMode] = useState('login');
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [convLoading, setConvLoading] = useState(false);

  useEffect(() => {
    if (!techUser) return;
    setConvLoading(true);
    getConversationsForUser(techUser?.id, 'tech')?.then(setConversations)?.catch(console.error)?.finally(() => setConvLoading(false));
  }, [techUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // ── Auth screens ──────────────────────────────────────────────────────────
  if (!techUser) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Image
              src="/assets/images/S-2-1766376617209.jpeg"
              alt="My Doc Refer logo"
              className="h-12 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-foreground">Technologist Portal</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {authMode === 'login' ? 'Sign in to your technologist account' : 'Create your technologist account'}
            </p>
          </div>

          <div className="clinical-card p-6 sm:p-8">
            <div className="flex gap-1 p-1 bg-muted rounded-lg mb-6">
              {['login', 'register']?.map((mode) => (
                <button
                  key={mode}
                  onClick={() => setAuthMode(mode)}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors capitalize ${
                    authMode === mode
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {mode === 'login' ? 'Sign In' : 'Register'}
                </button>
              ))}
            </div>

            {authMode === 'login'
              ? <TechLoginForm onSwitchToRegister={() => setAuthMode('register')} />
              : <TechRegisterForm onSwitchToLogin={() => setAuthMode('login')} />
            }
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Are you a doctor?{' '}
            <a href="/login" className="text-primary hover:underline">Doctor login →</a>
          </p>
        </div>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────
  const displayName = techProfile?.full_name || techUser?.email;
  const facility = techProfile?.facility || '';
  const specialization = techProfile?.specialization || '';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top bar */}
      <header className="h-16 bg-card border-b border-border flex items-center px-4 sm:px-6 gap-4 flex-shrink-0">
        <Image
          src="/assets/images/Screenshot_2025-12-22_at_12.13.42_AM-1766377944108.png"
          alt="My Doc Refer logo"
          className="h-9 w-auto object-contain"
        />
        <div className="flex-1" />
        <div className="flex items-center gap-3">
          <div className="hidden sm:block text-right">
            <p className="text-sm font-medium text-foreground leading-none">{displayName}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{specialization || 'Technologist'}</p>
          </div>
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="User" size={18} className="text-primary" />
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="LogOut" size={16} />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </header>
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-72 border-r border-border bg-card flex flex-col flex-shrink-0">
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Icon name="Stethoscope" size={20} className="text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">{facility}</p>
              </div>
            </div>
          </div>

          <div className="p-4 flex-1 overflow-y-auto">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Active Conversations
            </p>
            {convLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : conversations?.length === 0 ? (
              <div className="text-center py-8 space-y-2">
                <Icon name="MessageSquare" size={28} className="text-muted-foreground mx-auto" />
                <p className="text-xs text-muted-foreground">No conversations yet.</p>
                <p className="text-xs text-muted-foreground">Doctors will initiate chats from their dashboard.</p>
              </div>
            ) : (
              <div className="space-y-1">
                {conversations?.map((conv) => (
                  <button
                    key={conv?.id}
                    onClick={() => setActiveConv(conv)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      activeConv?.id === conv?.id
                        ? 'bg-primary/10 text-foreground'
                        : 'hover:bg-muted text-foreground'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={15} className="text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{conv?.doctor_name || 'Doctor'}</p>
                      <p className="text-xs text-muted-foreground truncate">{conv?.last_message || 'No messages yet'}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Chat area */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {activeConv ? (
            <TechChatWindow
              conversation={activeConv}
              currentUser={techUser?.id}
              currentName={displayName}
              currentRole="tech"
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Icon name="MessageCircle" size={30} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Technologist Chat Portal</h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                  Select a conversation from the sidebar to view messages from referring doctors.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2 w-full max-w-sm">
                {[
                  { icon: 'Clock', label: 'Real-time', desc: 'Instant messaging' },
                  { icon: 'Shield', label: 'Secure', desc: 'End-to-end encrypted' },
                  { icon: 'Bell', label: 'Notifications', desc: 'Stay informed' },
                  { icon: 'FileText', label: 'Case notes', desc: 'Share details' },
                ]?.map(({ icon, label, desc }) => (
                  <div key={label} className="p-3 bg-muted/50 rounded-lg text-left">
                    <Icon name={icon} size={16} className="text-primary mb-1.5" />
                    <p className="text-xs font-medium text-foreground">{label}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default function TechnologistPortal() {
  return (
    <>
      <Helmet>
        <title>Technologist Portal | My Doc Refer</title>
        <meta name="description" content="Diagnostic imaging technologist portal for real-time communication with referring doctors." />
      </Helmet>
      <TechAuthProvider>
        <TechPortalInner />
      </TechAuthProvider>
    </>
  );
}
